import type { ColDef } from 'ag-grid-community';
import { formatLongDateTime } from './dateFormat';

/**
 * REG-828: the COLUMNS sheet fields the rep / funder / lead source detail tabs
 * show next to their own columns. The *_submission_activity_v views carry
 * them (main_rep_label, merchant_phone, merchant_state, commission_percent,
 * commission_amount = amount x commission %, psf_dollar_amount, payment_calc =
 * amount x factor / term, deal_type, performance, lead_source_label,
 * marketing_brand, contact_method masked for reps per REG-841, and the
 * submission's created_at for the Submissions tabs' Created At).
 */
export interface SheetDealFields {
  mainRepName: string;
  merchantPhone: string;
  merchantState: string;
  commissionPct: number | null;
  commissionAmount: number | null;
  psf: number | null;
  payment: number | null;
  dealType: string;
  performance: string;
  leadSourceName: string;
  brand: string;
  contactMethod: string;
  /** submission.created_at: the Submissions list's Created At. */
  createdAt: string | null;
}

const num = (value: unknown): number | null => (value == null || value === '' ? null : Number(value));
const text = (value: unknown): string => (value == null ? '' : String(value));

/** The sheet fields of one *_submission_activity_v row. */
export function sheetDealFields(row: Record<string, unknown>): SheetDealFields {
  return {
    mainRepName: text(row.main_rep_label),
    merchantPhone: text(row.merchant_phone),
    merchantState: text(row.merchant_state),
    commissionPct: num(row.commission_percent),
    commissionAmount: num(row.commission_amount),
    psf: num(row.psf_dollar_amount),
    payment: num(row.payment_calc),
    dealType: text(row.deal_type),
    performance: text(row.performance),
    leadSourceName: text(row.lead_source_label),
    brand: text(row.marketing_brand),
    contactMethod: text(row.contact_method),
    createdAt: row.created_at == null ? null : String(row.created_at),
  };
}

/** The view columns sheetDealFields reads (for .select lists). */
export const SHEET_DEAL_SELECT = 'main_rep_label,merchant_phone,merchant_state,commission_percent,commission_amount,'
  + 'psf_dollar_amount,payment_calc,deal_type,performance,lead_source_label,marketing_brand,contact_method,created_at';

const dash = (value: unknown): string => (value == null || value === '' ? '-' : String(value));
const usd = (value: unknown): string => (value == null || value === ''
  ? '-'
  : `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);

/** agDateColumnFilter comparator: the filter's day against a timestamp's New York day. */
function compareDay(day: Date, value: string | null): number {
  if (!value) return -1;
  const ny = new Date(value).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const want = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
  return ny === want ? 0 : ny < want ? -1 : 1;
}

/** Column definitions for the sheet fields, keyed by the sheet's column name. */
export function sheetDealColumns<T extends SheetDealFields>(): Record<
  'mainRep' | 'phone' | 'state' | 'payment' | 'commissionPct' | 'commission' | 'psf'
  | 'leadSource' | 'dealType' | 'performance' | 'brand' | 'contactMethod' | 'createdAt',
  ColDef<T>
> {
  return {
    mainRep: { headerName: 'MAIN REP', colId: 'mainRep', field: 'mainRepName' as never, width: 150, tooltipField: 'mainRepName' as never, valueFormatter: (p) => dash(p.value) },
    phone: { headerName: 'PHONE', colId: 'phone', field: 'merchantPhone' as never, width: 140, valueFormatter: (p) => dash(p.value) },
    state: { headerName: 'STATE', colId: 'state', field: 'merchantState' as never, width: 90, valueFormatter: (p) => dash(p.value) },
    payment: { headerName: 'PAYMENT', colId: 'payment', field: 'payment' as never, type: 'rightAligned', width: 120, filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => usd(p.value) },
    commissionPct: { headerName: 'COMMISSION %', colId: 'commissionPct', field: 'commissionPct' as never, type: 'rightAligned', width: 130, filter: 'agNumberColumnFilter', cellClass: 'numeric',
      valueFormatter: (p) => (p.value == null ? '-' : `${Number(p.value)}%`) },
    commission: { headerName: 'COMMISSION $', colId: 'commission', field: 'commissionAmount' as never, type: 'rightAligned', width: 130, filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => usd(p.value) },
    psf: { headerName: 'PSF $', colId: 'psf', field: 'psf' as never, type: 'rightAligned', width: 110, filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => usd(p.value) },
    leadSource: { headerName: 'LEAD SOURCE', colId: 'leadSource', field: 'leadSourceName' as never, width: 150, filter: 'agSetColumnFilter', valueFormatter: (p) => dash(p.value) },
    dealType: { headerName: 'DEAL TYPE', colId: 'dealType', field: 'dealType' as never, width: 130, filter: 'agSetColumnFilter', valueFormatter: (p) => dash(p.value) },
    performance: { headerName: 'PERFORMANCE', colId: 'performance', field: 'performance' as never, width: 130, filter: 'agSetColumnFilter', valueFormatter: (p) => dash(p.value) },
    brand: { headerName: 'MARKETING BRAND', colId: 'brand', field: 'brand' as never, width: 150, filter: 'agSetColumnFilter', valueFormatter: (p) => dash(p.value) },
    contactMethod: { headerName: 'CONTACT METHOD', colId: 'contactMethod', field: 'contactMethod' as never, width: 150, filter: 'agSetColumnFilter', valueFormatter: (p) => dash(p.value) },
    // The Submissions list's Created At, in the sheet's long format (New York time).
    createdAt: { headerName: 'CREATED AT', colId: 'createdAt', field: 'createdAt' as never, width: 330, sort: 'desc', filter: 'agDateColumnFilter',
      filterParams: { comparator: (day: Date, value: string | null) => compareDay(day, value) },
      valueFormatter: (p) => formatLongDateTime(p.value) || '-' },
  };
}
